import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FieldExpression, NodeExpression, term, toString as luceneToString } from 'lucene';

import { PaginatedResult } from '../../shared/models/paginated-result';
import { ORDER_SERVER_URL } from '../../config';
import { AggregateShipment, FieldOfShipment, FieldOfShipmentBatch, FieldOfShipmentCard, FieldOfShipmentOrder, Shipment,
	ShipmentBatch,
	shipmentBatchesFields,
	ShipmentCard,
	shipmentCardFields,
	ShipmentOrder,
	shipmentOrdersFields } from '../models/shipment';
import { OrderBy } from '../../shared/utils/constant';

const GET_SHIPMENT_AGGREGATE_URL = `${ ORDER_SERVER_URL }/v4/dataCenterShipments`;
const GET_SHIPMENT_URL = `${ ORDER_SERVER_URL }/v4/shipments`;

@Injectable({ providedIn: 'root' })
export class ShipmentsService {
	constructor(
		private readonly http: HttpClient,
	) {}

	getDatacenterShipments(
		fields: readonly FieldOfShipment[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof Shipment>[] | OrderBy<keyof Shipment>,
		onlyDistinct = false,
	) {
		const url = new URL(GET_SHIPMENT_AGGREGATE_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('distinct', String(onlyDistinct));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		return this.http.get<PaginatedResult<Shipment>>(url.toString());
	}

	getShipmentCard(
		id: string
	) {
		const fields: readonly FieldOfShipmentCard[] = shipmentCardFields;
		const shipmentId = encodeURIComponent(id);
		const url = new URL(`${ GET_SHIPMENT_URL }/${ shipmentId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		return this.http.get<ShipmentCard>(url.toString());
	}

	getBatchesByShipmentCard(
		id: string,
		index: number,
		limit: number,
	) {
		const shipmentId = encodeURIComponent(id);
		const url = new URL(`${ GET_SHIPMENT_URL }/${ shipmentId }/batches`);
		const params = url.searchParams;
		const fields: readonly FieldOfShipmentBatch[] = shipmentBatchesFields;
		params.append('fields', fields.join(','));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		return this.http.get<PaginatedResult<ShipmentBatch>>(url.toString());
	}

	getOrdersByShipmentCard(
		id: string,
		index: number,
		limit: number,
	) {
		const shipmentId = encodeURIComponent(id);
		const url = new URL(`${ GET_SHIPMENT_URL }/${ shipmentId }/orders`);
		const params = url.searchParams;
		const fields: readonly FieldOfShipmentOrder[] = shipmentOrdersFields;
		params.append('fields', fields.join(','));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		return this.http.get<PaginatedResult<ShipmentOrder>>(url.toString());
	}

	createLuceneFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;
		const dispatchAdviceNumberField: FieldExpression = {
			field: 'dispatchAdviceNumber',
			term: search,
			quoted: false,
		};

		const transportReferenceField: FieldExpression = {
			field: 'transportReferenceConcat',
			term: search,
			quoted: false,
		};

		const fullNodeExpression: NodeExpression = {
			left: dispatchAdviceNumberField,
			operator: 'OR',
			parenthesized: false,
			right: transportReferenceField
		};
		return luceneToString(fullNodeExpression);
	}

	getAggregatedQuantityForShipments(filter: string | undefined) {
		const url = new URL(`${ GET_SHIPMENT_URL }:aggregates`);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		return this.http.get<AggregateShipment>(url.toString());
	}
}
